import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, ShieldCheck } from "lucide-react";
import { WhipLogo } from "@/components/WhipLogo";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full rounded-lg border border-white/15 bg-white/[0.03] px-3 py-2.5 text-sm text-white placeholder-white/25 outline-none focus:border-orange-500/60";

const REASONS = {
  expired: "This setup link has expired. Links are valid for 48 hours.",
  used: "This setup link was already used. If you set a password, sign in below.",
  invalid: "This setup link is not valid. Check that the full link from your email was copied.",
};

const Shell = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center px-4 py-10" style={{ background: "var(--whip-bg)" }} data-testid="setup-password-page">
    <div className="w-full max-w-md">
      <div className="flex justify-center mb-8"><WhipLogo size={40} /></div>
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8 backdrop-blur">{children}</div>
    </div>
  </div>
);

export default function SetupPassword() {
  const { token } = useParams();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, valid: false, reason: "", email: "", name: "" });
  const [form, setForm] = useState({ next: "", confirm: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/auth/password-setup/${encodeURIComponent(token)}`)
      .then((r) => setState({ loading: false, ...r.data }))
      .catch(() => setState({ loading: false, valid: false, reason: "invalid" }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.next !== form.confirm) { setError("The two passwords do not match."); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/password-setup/complete`, { token, new_password: form.next });
      setUser(data.user);
      toast.success("Password set. Welcome to WHIP.");
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not set password");
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return <Shell><p className="text-sm text-white/55" data-testid="setup-password-checking">Checking your link…</p></Shell>;
  }

  if (!state.valid) {
    return (
      <Shell>
        <div className="flex items-center gap-2 text-rose-300 text-xs font-mono uppercase tracking-widest mb-3"><KeyRound className="w-4 h-4" /> Link problem</div>
        <h1 className="text-2xl font-bold text-white">We could not open this link</h1>
        <p className="text-sm text-white/55 mt-2" data-testid="setup-password-invalid">{REASONS[state.reason] || REASONS.invalid}</p>
        <div className="mt-6 flex flex-col gap-2">
          <Link to="/setup-link" className="w-full text-center rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 px-5 py-2.5 text-sm font-bold text-white hover:from-orange-500 hover:to-orange-400" data-testid="setup-password-request-new">Request a new link</Link>
          <Link to="/login" className="w-full text-center rounded-lg border border-white/15 px-5 py-2.5 text-sm text-white/70 hover:text-white" data-testid="setup-password-go-login">Go to sign in</Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="flex items-center gap-2 text-orange-400 text-xs font-mono uppercase tracking-widest mb-3"><ShieldCheck className="w-4 h-4" /> Welcome to WHIP</div>
      <h1 className="text-2xl font-bold text-white">Choose your password</h1>
      <p className="text-sm text-white/55 mt-2">
        Setting up <strong className="text-white/85" data-testid="setup-password-email">{state.email}</strong>. Pick a password only you know: at least 10 characters with a letter and a number. This link works once.
      </p>
      <form onSubmit={submit} className="mt-6 space-y-3">
        <input type="password" autoComplete="new-password" placeholder="New password" value={form.next} minLength={10}
          onChange={(e) => setForm({ ...form, next: e.target.value })} className={inputCls} required data-testid="setup-password-new-input" />
        <input type="password" autoComplete="new-password" placeholder="Confirm password" value={form.confirm}
          onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={inputCls} required data-testid="setup-password-confirm-input" />
        {error && <p className="text-sm text-rose-300" data-testid="setup-password-error">{error}</p>}
        <button type="submit" disabled={submitting}
          className="w-full rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 px-5 py-2.5 text-sm font-bold text-white hover:from-orange-500 hover:to-orange-400 transition-colors disabled:opacity-60"
          data-testid="setup-password-submit">
          {submitting ? "Saving…" : "Set password and sign in"}
        </button>
      </form>
    </Shell>
  );
}
